import {
  ApplicantTaskProgress,
  ApplicantTaskStatus,
  DocumentationAgency,
  DocumentationStep,
  DocumentationTask,
} from "../data/types";

export const TASK_STATUS_OPTIONS: { id: ApplicantTaskStatus; label: string; dot: string }[] = [
  { id: "not_started", label: "Not Started", dot: "bg-[#9CA3AF]" },
  { id: "submitted", label: "Submitted", dot: "bg-[#3B82F6]" },
  { id: "in_review", label: "In Review", dot: "bg-[#F59E0B]" },
  { id: "fix_required", label: "Fix Required", dot: "bg-[#EF4444]" },
  { id: "completed", label: "Completed", dot: "bg-[#22C55E]" },
];

/** Statuses an applicant may set on their own checklist. */
export const APPLICANT_WRITABLE_STATUSES: ApplicantTaskStatus[] = ["not_started", "submitted"];

const createId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `doc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const emptyAgency = (): DocumentationAgency => ({
  name: "",
  address: "",
  email: "",
  phone: "",
});

/** Create a distinct applicant task that describes the parent step. */
export const createDefaultTaskFromStep = (
  step: Pick<DocumentationStep, "title" | "description" | "agency">,
  order = 0
): DocumentationTask => ({
  id: createId(),
  title: step.title?.trim() || "Complete this step",
  description:
    step.description?.trim() ||
    "Complete the applicant action needed for this documentation checkpoint.",
  agency: step.agency ? { ...step.agency } : emptyAgency(),
  order,
});

export const createEmptyTask = (order = 0): DocumentationTask => ({
  id: createId(),
  title: "",
  description: "",
  agency: emptyAgency(),
  order,
});

/**
 * Every step must have at least one task. If none exist, add a distinct task
 * that describes the step — do not treat the step record itself as the task.
 */
export const ensureStepHasDefaultTask = (step: DocumentationStep): DocumentationStep => {
  if (step.tasks && step.tasks.length > 0) {
    return {
      ...step,
      tasks: step.tasks.map((task, index) => ({ ...task, order: task.order ?? index })),
    };
  }
  return {
    ...step,
    tasks: [createDefaultTaskFromStep(step, 0)],
  };
};

export const ensureStepsHaveDefaultTasks = (steps: DocumentationStep[]): DocumentationStep[] =>
  steps.map((step, index) =>
    ensureStepHasDefaultTask({
      ...step,
      order: step.order ?? index,
    })
  );

export const flattenStepTasks = (
  steps: DocumentationStep[] = []
): Array<DocumentationTask & { step_id: string; step_title?: string }> =>
  steps.flatMap((step) =>
    (step.tasks || []).map((task) => ({
      ...task,
      step_id: step.id,
      step_title: step.title,
    }))
  );

export const findProgress = (
  checklist: ApplicantTaskProgress[] | undefined,
  stepId: string,
  taskId: string
): ApplicantTaskProgress | undefined =>
  (checklist || []).find((item) => item.step_id === stepId && item.task_id === taskId);

export type ApplicantProgressSummary = {
  total: number
  completed: number
  byStatus: Record<ApplicantTaskStatus, number>
  newComments: number
};

export const emptyProgressSummary = (): ApplicantProgressSummary => ({
  total: 0,
  completed: 0,
  byStatus: {
    not_started: 0,
    submitted: 0,
    in_review: 0,
    fix_required: 0,
    completed: 0,
  },
  newComments: 0,
});

export const summarizeApplicantProgress = (
  steps: DocumentationStep[] = [],
  checklist: ApplicantTaskProgress[] = []
): ApplicantProgressSummary => {
  const summary = emptyProgressSummary();
  const tasks = flattenStepTasks(steps);
  summary.total = tasks.length;
  tasks.forEach((task) => {
    const progress = findProgress(checklist, task.step_id, task.id);
    const status = (progress?.status || "not_started") as ApplicantTaskStatus;
    summary.byStatus[status] += 1;
    if (status === "completed") summary.completed += 1;
    summary.newComments += progress?.activity?.length || 0;
  });
  return summary;
};

export const upsertProgressStatus = (
  checklist: ApplicantTaskProgress[] = [],
  stepId: string,
  taskId: string,
  status: ApplicantTaskStatus
): ApplicantTaskProgress[] => {
  const next = checklist.map((item) => ({ ...item, activity: item.activity ? [...item.activity] : [] }));
  const index = next.findIndex((item) => item.step_id === stepId && item.task_id === taskId);
  if (index >= 0) {
    next[index] = { ...next[index], status };
    return next;
  }
  next.push({ step_id: stepId, task_id: taskId, status, activity: [] });
  return next;
};

/**
 * Applicants may set Not Started or Submitted. From Fix Required they may
 * resubmit. In Review and Completed stay partner-only.
 */
export const canApplicantSetStatus = (
  nextStatus: ApplicantTaskStatus,
  currentStatus: ApplicantTaskStatus = "not_started"
): boolean => {
  if (nextStatus === currentStatus) return true;
  if (currentStatus === "in_review" || currentStatus === "completed") return false;
  return APPLICANT_WRITABLE_STATUSES.includes(nextStatus);
};

export const formatRelativeSubmitted = (created?: string): string => {
  if (!created) return "N/A";
  const then = new Date(created).getTime();
  if (Number.isNaN(then)) return "N/A";
  const diffMs = Date.now() - then;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

export const hasAgencyContact = (agency?: DocumentationAgency): boolean =>
  Boolean(agency?.name || agency?.email || agency?.address || agency?.phone);
